import { Box, Typography, Select, MenuItem, FormControl } from '@mui/material';
import { useTheme, type SxProps, type Theme } from '@mui/material/styles';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from '../../i18n';

interface LanguagePickerProps {
  rowSx: SxProps<Theme>;
}

/**
 * fr-locale Phase 3a: UI language picker rendered as a settings row matching
 * the dark-mode toggle's visual pattern.
 *
 * UI language vs report language: this picker drives the **UI** only. Workspace
 * report-output language is set at workspace creation (defaulted from this picker
 * via currentUiLanguage()) and cannot be changed afterward.
 */
export function LanguagePicker({ rowSx }: LanguagePickerProps) {
  const theme = useTheme();
  const { t, i18n } = useTranslation();

  const resolved = i18n.resolvedLanguage ?? 'en';
  const supported = SUPPORTED_LANGUAGES as readonly string[];
  const current: SupportedLanguage = supported.includes(resolved)
    ? (resolved as SupportedLanguage)
    : supported.includes(resolved.split('-')[0])
    ? (resolved.split('-')[0] as SupportedLanguage)
    : 'en';

  const labelKeyFor = (code: SupportedLanguage): string => {
    switch (code) {
      case 'en': return 'language.english';
      case 'fr': return 'language.french';
      case 'zh-CN': return 'language.chinese';
      default: return 'language.english';
    }
  };

  const handleChange = (next: SupportedLanguage) => {
    void i18n.changeLanguage(next);
  };

  return (
    <Box sx={rowSx}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
        <LanguageOutlinedIcon sx={{ fontSize: 20, color: theme.custom.colors.text.tertiary, flexShrink: 0 }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: theme.custom.typography.fontWeight.semibold,
              fontSize: theme.custom.typography.typeRoles.body.size,
              color: theme.custom.colors.text.primary,
              lineHeight: 1.4,
            }}
          >
            {t('language.label')}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontSize: theme.custom.typography.typeRoles.caption.size,
              color: theme.custom.colors.text.tertiary,
              lineHeight: 1.3,
            }}
          >
            {t('language.description')}
          </Typography>
        </Box>
      </Box>
      <FormControl size="small" sx={{ minWidth: 110, flexShrink: 0 }}>
        <Select
          value={current}
          onChange={(e) => handleChange(e.target.value as SupportedLanguage)}
          sx={{
            fontSize: theme.custom.typography.typeRoles.bodySm.size,
            borderRadius: theme.custom.borders.radius.md,
          }}
        >
          {SUPPORTED_LANGUAGES.map((code) => (
            <MenuItem key={code} value={code}>
              {t(labelKeyFor(code))}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
}
